// src/components/Groups/GroupPostComposer.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Send,
  X,
  FileText,
  Newspaper,
  Link as LinkIcon,
  Plus,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Info,
  Check,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Calendar,
  Loader
} from 'lucide-react';
import { InfoTooltip } from '../../Dashboard/UnifiedDashboard/Tooltip';
import { InvestmentDisclaimerNotice, AIProcessingNotice, CommunityGuidelinesNotice } from '../../../components/Compliance/ComplianceNotices';
import CredibilityExplainer from '../../../components/Credibility/CredibilityExplainer';

// Redux Actions (Paths mirrored from PostForm.jsx)

import { fetchEdgarFilingsData } from '../../../redux/stockData/edgar/edgarActions';
import { fetchSentimentData } from '../../../redux/stockSentiment/sentimentActions';
import { bullGroupPost, bearGroupPost } from '../../../redux/userGroups/groupPost/userGroupesPosts';
import { fireConfettiBurst } from '../../../utils/celebration';
import useMentionAutocomplete from './useMentionAutocomplete';
import MentionSuggestionDropdown from './MentionSuggestionDropdown';
import UserAvatar from '../../../components/UserAvatar/UserAvatar';

// ---------------------------------------------------------------------------
// Constants & Utilities
// ---------------------------------------------------------------------------

const MAX_EVIDENCE = 5;
const AUTO_DETECT_DEBOUNCE = 500;

const EVIDENCE_TYPE_LABEL_KEYS = {
  news_article: 'evidenceTypeNews',
  sec_filing: 'evidenceTypeSec',
  other: 'evidenceTypeOther',
};
const getEvidenceTypeLabel = (type, t) =>
  t(`groups.postComposer.${EVIDENCE_TYPE_LABEL_KEYS[type] || 'evidenceTypeOther'}`);

const EVIDENCE_TYPE_META = {
  news_article: {
    label: 'News Article',
    icon: Newspaper,
    color: {
      bg: 'bg-blue-50 dark:bg-blue-600/10',
      border: 'border-blue-300 dark:border-blue-500/30',
      text: 'text-blue-700 dark:text-blue-300',
      icon: 'text-blue-700 dark:text-blue-400',
      gradient: 'from-blue-600/20 to-blue-500/10 border-blue-500/30',
      badge: 'bg-blue-100 text-blue-700 border border-blue-300 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/30',
    },
  },
  sec_filing: {
    label: 'SEC Filing',
    icon: FileText,
    color: {
      bg: 'bg-violet-50 dark:bg-purple-600/10',
      border: 'border-violet-300 dark:border-purple-500/30',
      text: 'text-violet-700 dark:text-purple-300',
      icon: 'text-violet-700 dark:text-purple-400',
      gradient: 'from-purple-600/20 to-purple-500/10 border-purple-500/30',
      badge: 'bg-violet-100 text-violet-700 border border-violet-300 dark:bg-purple-500/20 dark:text-purple-300 dark:border-purple-500/30',
    },
  },
  other: {
    label: 'External Link',
    icon: LinkIcon,
    color: {
      bg: 'bg-gray-500/10 dark:bg-gray-600/10',
      border: 'border-gray-400/30 dark:border-gray-500/30',
      text: 'text-gray-600 dark:text-gray-300',
      icon: 'text-gray-600 dark:text-gray-400',
      gradient: 'from-gray-500/20 to-gray-400/10 dark:from-gray-600/20 dark:to-gray-500/10 border-gray-400/30 dark:border-gray-500/30',
      badge: 'bg-gray-500/20 text-gray-600 dark:text-gray-300 border border-gray-400/30 dark:border-gray-500/30',
    },
  },
};

/**
 * Extract stock symbols from text (e.g. $AAPL)
 */
const extractStockSymbols = (text) => {
  if (!text) return [];
  const symbolRegex = /\$([A-Za-z]{1,5})(?=\b|[^A-Za-z0-9])/g;
  const matches = [...text.matchAll(symbolRegex)];
  return [...new Set(matches.map(m => m[1].toUpperCase()))];
};

/**
 * Remove duplicate stocks by symbol (case-insensitive)
 */
const uniqueBySymbol = (arr) =>
  arr.filter(
    (s, i) =>
      arr.findIndex(
        x => x.symbol?.toUpperCase() === s.symbol?.toUpperCase()
      ) === i
  );

// ---------------------------------------------------------------------------
// EvidenceChip — compact attached-evidence display inside composer
// ---------------------------------------------------------------------------

const EvidenceChip = ({ item, onRemove }) => {
  const { t } = useTranslation();
  const meta = EVIDENCE_TYPE_META[item.evidence_type] || EVIDENCE_TYPE_META.other;
  const Icon = meta.icon;
  const colors = meta.color;

  return (
    <div className={`flex items-start gap-3 p-3 bg-gradient-to-r ${colors.gradient} border rounded-lg group transition-all`}>
      <div className={`flex-shrink-0 mt-0.5 ${colors.icon}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded ${colors.badge} font-medium`}>
                {getEvidenceTypeLabel(item.evidence_type, t)}
              </span>
            </div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1">{item.title}</p>
            {item.excerpt && (
              <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mt-0.5 leading-relaxed">{item.excerpt}</p>
            )}
            {item.source_url && item.source_url !== '#' && (
              <a
                href={item.source_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 mt-1 transition-colors"
              >
                <span className="truncate max-w-[220px]">{item.source_url}</span>
                <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            )}
            {item.relevance_note && (
              <div className="flex items-start gap-1.5 mt-1.5 pt-1.5 border-t border-gray-200 dark:border-gray-700/40">
                <Info className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-gray-600 dark:text-gray-300 italic leading-relaxed">"{item.relevance_note}"</p>
              </div>
            )}
          </div>
          <button
            onClick={() => onRemove(item._clientId)}
            className="flex-shrink-0 p-1.5 hover:bg-red-100 dark:hover:bg-red-500/10 rounded transition-colors"
            title={t('groups.postComposer.removeEvidence')}
          >
            <X className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400 hover:text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// EvidenceFormModal — add a single evidence item manually
// ---------------------------------------------------------------------------

const EvidenceFormModal = ({ onAdd, onClose, existingCount, initialTitle = '' }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    evidence_type: 'other', // Default to other/link for manual entry
    source_url: '',
    title: initialTitle,
    excerpt: '',
    relevance_note: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    setForm((f) => ({ ...f, title: initialTitle }));
  }, [initialTitle]);

  const handleAdd = () => {
    if (!form.title.trim()) {
      setError(t('groups.postComposer.titleRequired'));
      return;
    }
    if (!form.source_url.trim()) {
      setError(t('groups.postComposer.urlRequired'));
      return;
    }
    try {
      new URL(form.source_url.trim());
    } catch {
      setError(t('groups.postComposer.invalidUrl'));
      return;
    }
    onAdd({ ...form, source_url: form.source_url.trim(), title: form.title.trim() });
  };

  return (
    <div className="bg-cream-100 dark:bg-white/5 border border-cream-300 dark:border-gray-600/50 rounded-xl p-5 shadow-2xl space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{t('groups.postComposer.attachCustomLink')}</h4>
        <button
          onClick={onClose}
          className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-cream-900/10 dark:hover:bg-gray-700/60 rounded-lg transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {Object.entries(EVIDENCE_TYPE_META).map(([key, m]) => {
          const BtnIcon = m.icon;
          const active = form.evidence_type === key;
          return (
            <button
              key={key}
              onClick={() => setForm((f) => ({ ...f, evidence_type: key }))}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                active
                  ? `${m.color.bg} ${m.color.text} ${m.color.border}`
                  : 'bg-cream-100 dark:bg-gray-700/30 text-gray-600 dark:text-gray-400 border-cream-300 dark:border-gray-600/30 hover:bg-cream-900/10 dark:hover:bg-gray-700/50'
              }`}
            >
              <BtnIcon className="w-3.5 h-3.5" />
              {getEvidenceTypeLabel(key, t)}
            </button>
          );
        })}
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">{t('groups.postComposer.titleLabel')}</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => { setForm((f) => ({ ...f, title: e.target.value })); setError(''); }}
            placeholder={t('groups.postComposer.titlePlaceholder')}
            className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600/50 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">{t('groups.postComposer.sourceUrlLabel')}</label>
          <input
            type="url"
            value={form.source_url}
            onChange={(e) => { setForm((f) => ({ ...f, source_url: e.target.value })); setError(''); }}
            placeholder={t('groups.postComposer.sourceUrlPlaceholder')}
            className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600/50 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all font-mono"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">
            {t('groups.postComposer.summaryLabel')} <span className="text-gray-400 dark:text-gray-600">{t('groups.postComposer.optionalSuffix')}</span>
          </label>
          <textarea
            value={form.excerpt}
            onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
            placeholder={t('groups.postComposer.summaryPlaceholder')}
            rows={2}
            className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600/50 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 resize-none transition-all"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">
            {t('groups.postComposer.relevanceLabel')} <span className="text-gray-400 dark:text-gray-600">{t('groups.postComposer.optionalSuffix')}</span>
          </label>
          <input
            type="text"
            value={form.relevance_note}
            onChange={(e) => setForm((f) => ({ ...f, relevance_note: e.target.value }))}
            placeholder={t('groups.postComposer.relevancePlaceholder')}
            className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600/50 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
          />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-xs text-red-400">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2 bg-cream-100 dark:bg-gray-700/40 hover:bg-cream-900/10 dark:hover:bg-gray-700/60 text-gray-600 dark:text-gray-300 text-sm rounded-lg transition-colors"
        >
          {t('groups.postComposer.cancel')}
        </button>
        <button
          onClick={handleAdd}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600/80 hover:bg-blue-600 text-white text-sm rounded-lg font-medium transition-colors"
        >
          <Check className="w-4 h-4" />
          {t('groups.postComposer.attachButton', { count: existingCount + 1, max: MAX_EVIDENCE })}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// GroupPostComposer — main export
// ---------------------------------------------------------------------------

const GroupPostComposer = ({
  groupId,
  currentUser,
  onCreatePost,
  onClose,
  defaultExpanded = true,
  groupStocks = [],
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Redux Selectors for Auto-Evidence
  const edgarState = useSelector(state => state.edgar || {});
  const sentimentState = useSelector(state => state.sentiment || {});
  const filings = useMemo(() => edgarState.filings || [], [edgarState.filings]);
  const newsArticles = useMemo(() => sentimentState.sentimentData?.articles || [], [sentimentState.sentimentData]);

  // UI State
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const contentTextareaRef = useRef(null);
  const mention = useMentionAutocomplete();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  // 'bull' | 'bear' | null -- optional, explicit self-vote captured at
  // compose time. Deliberately NOT AI-inferred: credibility_service.py's
  // _author_direction_for_group_post only counts a prediction when the
  // author explicitly self-voted (fifty-eighth pass, closed the most
  // severe gaming vector found this engagement -- a post the author never
  // took a stated position on could otherwise register as a scored
  // "prediction" via crowd votes). AI enrichment already auto-extracts a
  // separate, smaller "model" bonus from post text independently of this;
  // this selector is what feeds the much larger self-vote-gated pathway,
  // just moved into the compose flow instead of a separate action a user
  // has to remember to take afterward on the feed.
  const [callDirection, setCallDirection] = useState(null);
  // MAR (Market Abuse Regulation) Art 20: a person publishing an investment
  // recommendation must disclose whether they hold a position in the asset,
  // presented alongside the recommendation -- not just captured and logged.
  // See 08_MASTER_TODO.md #62.
  const [disclosesPosition, setDisclosesPosition] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Confetti fires exactly once per successful publish, matching
  // PostForm.jsx's own celebration pattern for the main post composer.
  const hasCelebratedRef = useRef(false);
  useEffect(() => {
    if (showSuccess && !hasCelebratedRef.current) {
      hasCelebratedRef.current = true;
      fireConfettiBurst();
    } else if (!showSuccess) {
      hasCelebratedRef.current = false;
    }
  }, [showSuccess]);

  // Evidence State
  const [evidenceItems, setEvidenceItems] = useState([]);
  const [showEvidenceForm, setShowEvidenceForm] = useState(false);
  const [showEvidencePicker, setShowEvidencePicker] = useState(false);
  const [evidenceInitialTitle, setEvidenceInitialTitle] = useState('');
  
  // Stocks State (Group stocks + Keyed in stocks)
  const [activeStocks, setActiveStocks] = useState([]);
  const [isFetchingData, setIsFetchingData] = useState(false);

  // Length bounds match the backend's GroupPostBase Field(min_length=10,
  // max_length=200) / Field(min_length=1, max_length=10000) constraints --
  // previously only checked non-empty, same gap as StockerPostComposer.jsx.
  const isValid =
    title.trim().length >= 10 &&
    title.trim().length <= 200 &&
    content.trim().length > 0 &&
    content.trim().length <= 10000 &&
    !!groupId;
  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;
  const canAddEvidence = evidenceItems.length < MAX_EVIDENCE;
  const hasAvailableData = filings.length > 0 || newsArticles.length > 0;

  // Initialize group stocks
  useEffect(() => {
    if (groupStocks && groupStocks.length > 0) {
      setActiveStocks(uniqueBySymbol(groupStocks));
    }
  }, [groupStocks]);

  // Auto-detect "$TICKER" symbols typed into the content
  useEffect(() => {
    const detectStockSymbols = () => {
      const detectedSymbols = extractStockSymbols(content);
      if (detectedSymbols.length === 0) return;

      let hasNew = false;
      const newStocks = [...activeStocks];

      detectedSymbols.forEach(symbol => {
        const alreadyAdded = newStocks.some(s => s.symbol?.toUpperCase() === symbol);
        if (!alreadyAdded) {
          newStocks.push({ id: symbol, symbol: symbol, name: symbol });
          hasNew = true;
          // Fetch data for the newly detected stock
          dispatch(fetchEdgarFilingsData(symbol));
          dispatch(fetchSentimentData(symbol));
        }
      });

      if (hasNew) {
        setActiveStocks(uniqueBySymbol(newStocks));
      }
    };

    const timer = setTimeout(detectStockSymbols, AUTO_DETECT_DEBOUNCE);
    return () => clearTimeout(timer);
  }, [content, activeStocks, dispatch]);

  const handleAddEvidence = useCallback((item) => {
    setEvidenceItems((prev) => [...prev, { ...item, _clientId: Date.now() + Math.random() }]);
    setShowEvidenceForm(false);
    setEvidenceInitialTitle('');
  }, []);

  const handleRemoveEvidence = useCallback((clientId) => {
    setEvidenceItems((prev) => prev.filter((e) => e._clientId !== clientId));
  }, []);

  const handleCloseEvidenceForm = useCallback(() => {
    setShowEvidenceForm(false);
    setEvidenceInitialTitle('');
  }, []);

  // Quick Add Evidence from the Picker
  const quickAddEvidence = (item, type) => {
    if (!canAddEvidence) return;

    const evidence = {
      _clientId: Date.now() + Math.random(),
      evidence_type: type, // 'sec_filing' or 'news_article'
      source_url: item.url || item.filing_url || item.source_url || '#',
      title: item.title || item.form || t('groups.postComposer.untitledFallback'),
      excerpt: item.summary || item.filing_type || '',
      relevance_note: ''
    };

    setEvidenceItems((prev) => [...prev, evidence]);
    setShowEvidencePicker(false);
  };

  // When a stock chip is clicked, fetch its latest data and open the stock data picker
  const handleStockChipClick = async (stock) => {
    setShowEvidenceForm(false);
    setShowEvidencePicker(true);
    setIsFetchingData(true);
    
    try {
      await Promise.all([
        dispatch(fetchEdgarFilingsData(stock.symbol)),
        dispatch(fetchSentimentData(stock.symbol))
      ]);
    } finally {
      setIsFetchingData(false);
    }
  };

  const handleSubmit = async () => {
    if (!isValid || isSubmitting) return;
    setSubmitError('');
    setIsSubmitting(true);

    // Strip internal _clientId before sending
    const evidencePayload = evidenceItems.map(({ _clientId, ...rest }) => rest);

    try {
      const result = await onCreatePost?.({
        title: title.trim(),
        content: content.trim(),
        evidence: evidencePayload,
        disclosesPosition,
      });

      // Cast the self-vote captured above as part of the same action, not a
      // separate step the user has to remember on the feed afterward. Fire
      // this after the post itself is confirmed created; a failure here
      // shouldn't make the user think their post failed, so it's logged,
      // not surfaced as a submit error.
      const newPostId = result?.payload?.id;
      if (callDirection && newPostId) {
        try {
          await dispatch((callDirection === 'bull' ? bullGroupPost : bearGroupPost)({ postId: newPostId }));
        } catch (voteErr) {
          console.error('Failed to record self-vote on new post:', voteErr);
        }
      }

      setShowSuccess(true);
      setTimeout(() => {
        setTitle('');
        setContent('');
        setEvidenceItems([]);
        setShowEvidenceForm(false);
        setShowEvidencePicker(false);
        setEvidenceInitialTitle('');
        setCallDirection(null);
        setDisclosesPosition(false);
        setShowSuccess(false);
        setIsSubmitting(false);
      }, 2000);
    } catch (err) {
      // createGroupPost's thunk calls rejectWithValue(stringMessage), and
      // .unwrap() throws that payload directly (not an Error) -- reading
      // err?.message here always missed it and fell back to the generic
      // message, hiding e.g. the verify-your-email action gate's real text.
      setSubmitError(typeof err === 'string' ? err : (err?.message || t('groups.postComposer.submitErrorFallback')));
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="bg-gradient-to-r from-emerald-600/10 to-blue-600/10 border border-emerald-500/20 rounded-2xl p-6">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center animate-jiggle">
            <Check className="w-6 h-6 text-emerald-400" />
          </div>
          <div className="text-center">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('groups.postComposer.posted')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('groups.postComposer.sharedWithGroup')}
              {evidenceItems.length > 0 &&
                ` ${t('groups.postComposer.successEvidenceCount', { count: evidenceItems.length })}`}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-cream-100 to-cream-200 dark:from-gray-800/80 dark:to-gray-900/80 backdrop-blur-sm border border-blue-500/20 rounded-2xl overflow-hidden shadow-2xl">
      {/* ── Header ── */}
      <div
        className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-600/10 to-purple-600/10 border-b border-cream-300 dark:border-gray-700/50 cursor-pointer hover:bg-blue-600/5 transition-colors select-none"
        onClick={() => setIsExpanded((v) => !v)}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <Send className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">{t('groups.postComposer.shareYourAnalysis')}</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">{t('groups.postComposer.attachSourcesSubtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {evidenceItems.length > 0 && (
            <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded-full">
              {t('groups.postComposer.sourceCount', { count: evidenceItems.length })}
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          )}
          {onClose && (
            <button
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-cream-900/10 dark:hover:bg-gray-700/60 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      {isExpanded && (
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <UserAvatar userId={currentUser?.id} username={currentUser?.username} className="w-9 h-9" textClass="text-sm" />
            <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">{currentUser?.username || t('groups.postComposer.youFallback')}</span>
          </div>

          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('groups.postComposer.postTitlePlaceholder')}
            maxLength={200}
            className="w-full px-4 py-3 bg-white dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent text-sm transition-all"
            disabled={isSubmitting}
          />

          <div className="relative">
            <textarea
              ref={contentTextareaRef}
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                mention.handleTextChange(e.target.value, e.target.selectionStart ?? e.target.value.length);
              }}
              onKeyDown={(e) => {
                const result = mention.handleKeyDown(e.key);
                if (result === undefined) return;
                e.preventDefault();
                if (result) {
                  const cursor = contentTextareaRef.current?.selectionStart ?? content.length;
                  const { text, cursorIndex } = mention.applyMention(content, cursor, result);
                  setContent(text);
                  requestAnimationFrame(() => {
                    contentTextareaRef.current?.setSelectionRange(cursorIndex, cursorIndex);
                    contentTextareaRef.current?.focus();
                  });
                }
              }}
              onBlur={mention.closeSuggestions}
              placeholder={t('groups.postComposer.contentPlaceholder')}
              rows={4}
              className="w-full px-4 py-3 bg-white dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent resize-none text-sm transition-all"
              disabled={isSubmitting}
            />
            {mention.isActive && (
              <MentionSuggestionDropdown
                suggestions={mention.suggestions}
                highlightedIndex={mention.highlightedIndex}
                loading={mention.loading}
                onSelect={(user) => {
                  const cursor = contentTextareaRef.current?.selectionStart ?? content.length;
                  const { text, cursorIndex } = mention.applyMention(content, cursor, user);
                  setContent(text);
                  requestAnimationFrame(() => {
                    contentTextareaRef.current?.setSelectionRange(cursorIndex, cursorIndex);
                    contentTextareaRef.current?.focus();
                  });
                }}
              />
            )}
            <div className="flex items-center justify-between mt-1 px-1">
              <span className="text-xs text-gray-500 dark:text-gray-500">
                {t('groups.postComposer.wordCount', { count: wordCount })}
              </span>
              {evidenceItems.length > 0 && (
                <span className="text-xs text-blue-400">
                  {t('groups.postComposer.evidenceAttachedCount', { count: evidenceItems.length })}
                </span>
              )}
            </div>
          </div>

          {/* Attached Evidence Chips */}
          {evidenceItems.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                <FileText className="w-3.5 h-3.5" />
                <span>{t('groups.postComposer.evidenceHeading', { count: evidenceItems.length, max: MAX_EVIDENCE })}</span>
              </div>
              {evidenceItems.map((item) => (
                <EvidenceChip key={item._clientId} item={item} onRemove={handleRemoveEvidence} />
              ))}
            </div>
          )}

          {/* Dynamic Stock Tag Chips (Group + Auto-detected) */}
          {activeStocks.length > 0 && evidenceItems.length < MAX_EVIDENCE && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                <TrendingUp className="w-3.5 h-3.5 text-blue-400" />
                <span>{t('groups.postComposer.quickAddEvidence')}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {activeStocks.map((stock) => (
                  <button
                    key={stock.id || stock.symbol}
                    onClick={() => handleStockChipClick(stock)}
                    disabled={isSubmitting || isFetchingData}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-100 dark:bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 hover:border-blue-500/50 rounded-full text-xs text-blue-300 hover:text-blue-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    title={t('groups.postComposer.viewEvidenceDataTitle', { symbol: stock.symbol })}
                  >
                    <TrendingUp className="w-3 h-3" />
                    <span className="font-medium">{stock.symbol}</span>
                    {stock.name && stock.name !== stock.symbol && (
                      <span className="text-blue-400/60 hidden sm:inline truncate max-w-[80px]">
                        {stock.name}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Your Call -- optional, explicit self-vote captured at compose
              time instead of a separate action on the feed afterward.
              Deliberately not required: a post can be a question, news
              share, or discussion with no directional call at all. */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-500">
              <span>
                {t('groups.postComposer.yourCall')}{' '}
                {activeStocks.length > 0
                  ? t('groups.postComposer.yourCallOn', { symbols: activeStocks.map((s) => s.symbol).join(', ') })
                  : t('groups.postComposer.yourCallOptional')}
              </span>
              <InfoTooltip content={t('groups.postComposer.yourCallTooltip')} />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCallDirection((d) => (d === 'bull' ? null : 'bull'))}
                disabled={isSubmitting}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  callDirection === 'bull'
                    ? 'bg-green-500/20 border-green-500/50 text-green-300'
                    : 'bg-cream-100 dark:bg-gray-800/50 border-cream-300 dark:border-gray-600/30 text-gray-600 dark:text-gray-400 hover:bg-cream-900/10 dark:hover:bg-gray-700/50 hover:text-green-700 dark:hover:text-green-400'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                {t('groups.postComposer.bullish')}
              </button>
              <button
                type="button"
                onClick={() => setCallDirection((d) => (d === 'bear' ? null : 'bear'))}
                disabled={isSubmitting}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  callDirection === 'bear'
                    ? 'bg-red-500/20 border-red-500/50 text-red-300'
                    : 'bg-cream-100 dark:bg-gray-800/50 border-cream-300 dark:border-gray-600/30 text-gray-600 dark:text-gray-400 hover:bg-cream-900/10 dark:hover:bg-gray-700/50 hover:text-red-700 dark:hover:text-red-400'
                }`}
              >
                <TrendingDown className="w-4 h-4" />
                {t('groups.postComposer.bearish')}
              </button>
            </div>
            <CredibilityExplainer label={t('credibility.explainer.groupPostLabel')} />
          </div>

          {/* Position disclosure -- MAR Art 20. Was gated behind
              callDirection (the Bull/Bear self-vote below), so a
              purely-prose directional call with no vote click never
              triggered the compliance prompt. Now shown whenever there's
              real content to disclose against, independent of that vote --
              the vote itself (callDirection) is untouched, this only
              affects when the disclosure checkbox appears. */}
          {content.trim().length > 0 && (
            <label className="flex items-start gap-2.5 px-3.5 py-3 bg-amber-500/5 border border-amber-500/20 rounded-xl cursor-pointer select-none">
              <input
                type="checkbox"
                checked={disclosesPosition}
                onChange={(e) => setDisclosesPosition(e.target.checked)}
                disabled={isSubmitting}
                className="mt-0.5 w-4 h-4 accent-amber-500 flex-shrink-0"
              />
              <span className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                <span className="font-medium text-gray-900 dark:text-white">{t('groups.postComposer.holdPosition')}</span>{' '}
                {activeStocks.length > 0
                  ? t('groups.postComposer.inTickers', { symbols: activeStocks.map((s) => s.symbol).join(', ') })
                  : t('groups.postComposer.inAssetsDiscussed')}{' '}
                {t('groups.postComposer.shownOnPublishedPost')}
              </span>
            </label>
          )}

          {/* Action Buttons for Evidence */}
          {canAddEvidence && (
            <div className="flex flex-col gap-2 mt-2">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-500">
                <span>{t('groups.postComposer.evidenceLabel')}</span>
                <InfoTooltip content={t('groups.postComposer.evidenceTooltip')} />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => {
                    setShowEvidencePicker(!showEvidencePicker);
                    setShowEvidenceForm(false);
                  }}
                  disabled={activeStocks.length === 0 || isSubmitting}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg text-sm text-blue-300 transition-all flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {showEvidencePicker ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  <span>{t('groups.postComposer.autoSelectData')}</span>
                </button>
                <button
                  onClick={() => {
                    setShowEvidenceForm(true);
                    setShowEvidencePicker(false);
                  }}
                  disabled={isSubmitting}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-cream-100 dark:bg-gray-700/50 hover:bg-cream-900/10 dark:hover:bg-gray-700/70 border border-cream-300 dark:border-gray-600/50 rounded-lg text-sm text-gray-600 dark:text-gray-300 transition-all flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus size={16} />
                  <span>{t('groups.postComposer.customLink')}</span>
                </button>
              </div>
            </div>
          )}

          {/* Evidence Picker Dropdown UI (Replicated from PostForm.jsx) */}
          {showEvidencePicker && canAddEvidence && (
            <div className="bg-cream-100 dark:bg-gray-800/60 border border-cream-300 dark:border-gray-700 rounded-xl p-4 space-y-4 max-h-96 overflow-y-auto mt-2">
              {isFetchingData ? (
                <div className="flex flex-col items-center justify-center py-6 text-gray-600 dark:text-gray-400">
                  <Loader className="w-6 h-6 animate-spin mb-2" />
                  <span className="text-sm">{t('groups.postComposer.fetchingStockData')}</span>
                </div>
              ) : (
                <>
                  {/* SEC Filings Section */}
                  {filings.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <FileText size={16} className="text-purple-400" />
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{t('groups.postComposer.secFilingsHeading')}</h4>
                        <span className="text-xs text-gray-500 dark:text-gray-500">{t('groups.postComposer.availableCount', { count: filings.length })}</span>
                      </div>
                      <div className="space-y-2">
                        {filings.slice(0, 5).map((filing, idx) => (
                          <button
                            key={idx}
                            onClick={() => quickAddEvidence(filing, 'sec_filing')}
                            className="w-full text-left p-3 bg-purple-600/10 hover:bg-purple-600/20 border border-purple-500/20 rounded-lg transition-all group"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <span className="text-xs font-mono font-bold text-purple-300 bg-purple-900/40 px-2 py-0.5 rounded">
                                    {filing.form || t('groups.postComposer.filingFallback')}
                                  </span>
                                  {filing.filing_date && (
                                    <span className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                                      <Calendar size={12} />
                                      {new Date(filing.filing_date).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                                  {filing.summary || filing.filing_type || t('groups.postComposer.secFilingFallback')}
                                </p>
                              </div>
                              <Plus size={16} className="text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* News Articles Section */}
                  {newsArticles.length > 0 && (
                    <div className={filings.length > 0 ? "pt-2 border-t border-gray-200 dark:border-gray-700/50" : ""}>
                      <div className="flex items-center gap-2 mb-3">
                        <Newspaper size={16} className="text-blue-400" />
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{t('groups.postComposer.recentNewsHeading')}</h4>
                        <span className="text-xs text-gray-500 dark:text-gray-500">{t('groups.postComposer.availableCount', { count: newsArticles.length })}</span>
                      </div>
                      <div className="space-y-2">
                        {newsArticles.slice(0, 5).map((article, idx) => (
                          <button
                            key={idx}
                            onClick={() => quickAddEvidence(article, 'news_article')}
                            className="w-full text-left p-3 bg-blue-600/10 hover:bg-blue-600/20 border border-blue-500/20 rounded-lg transition-all group"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2 mb-1">
                                  {article.title}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 flex-wrap">
                                  {article.source && <span>{article.source}</span>}
                                  {article.time_published && (
                                    <>
                                      <span>•</span>
                                      <span>{new Date(article.time_published).toLocaleDateString()}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <Plus size={16} className="text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {!hasAvailableData && (
                    <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                      <FileText size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{t('groups.postComposer.noDataAvailable')}</p>
                      <p className="text-xs mt-1">{t('groups.postComposer.tryAnotherStockTag')}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Manual Custom Evidence Form Modal */}
          {showEvidenceForm && (
            <EvidenceFormModal
              onAdd={handleAddEvidence}
              onClose={handleCloseEvidenceForm}
              existingCount={evidenceItems.length}
              initialTitle={evidenceInitialTitle}
            />
          )}

          {/* Error Message Display */}
          {submitError && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-100 dark:bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-700 dark:text-red-400">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {submitError}
            </div>
          )}

          {/* Compliance notices -- persistent, not dismissible, shown at the
              actual point of posting rather than relying solely on the
              one-time signup acknowledgment (see ComplianceNotices.jsx). */}
          <div className="space-y-2">
            <InvestmentDisclaimerNotice compact />
            <AIProcessingNotice />
            <CommunityGuidelinesNotice />
          </div>

          {/* Submit Action */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleSubmit}
              disabled={!isValid || isSubmitting}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                isValid && !isSubmitting
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg shadow-blue-500/20'
                  : 'bg-cream-100 dark:bg-gray-700/30 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>{t('groups.postComposer.publishing')}</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>{t('groups.postComposer.publishPost')}</span>
                </>
              )}
            </button>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-500 text-center leading-relaxed">
            {t('groups.postComposer.footerNote')}
          </p>
        </div>
      )}
    </div>
  );
};

export default GroupPostComposer;